import { startReplace } from './image-panel.js';
import {
  applyDesignerCommand,
  designerTemplate,
  designerTemplateKind,
  DESIGNER_TEMPLATES,
  DESIGNER_COLORWAYS,
  type DesignerColorway,
  type DesignerTemplateKind,
  type DesignAreaAnswer,
  type DesignLayer,
} from '@pz/scene';
import { el, field, note, searchBox, tabs, type DesignerCtx } from './panels.js';
import { run, layerLabel, templateUnreplaced } from './commands.js';
import { fillText } from './texts.js';
import { paintThumb } from './thumbs.js';
import { pickPhoto } from './quick-start.js';
import { freshImageLayer } from './state.js';
import { hexToRgb, luminance } from './contrast.js';

/**
 * "Šabloni": ready-made layouts, sorted the way a shopper thinks about them —
 * Slika + tekst, Tekst, Slika — and the quick fields for whatever the current
 * design has authored as a template object.
 *
 * A card shows the layout WITH the shopper's own photo and name already in
 * it, and one tap applies it (one undo step). A photo layout tapped with no
 * photo in the design opens the photo picker first. The words carry over by
 * field key ("ime", "datum"…), and a plain name the shopper typed becomes the
 * layout's name — so trying five templates never means typing a name five
 * times.
 */

type KindFilter = 'all' | DesignerTemplateKind;

interface TemplateFilter {
  query: string;
  category: string;
  kind?: KindFilter;
  colorway: DesignerColorway;
  /** The shopper picked a colourway; stop choosing one for them. */
  colorPicked?: boolean;
}

const filters = new WeakMap<DesignerCtx, TemplateFilter>();

function filterFor(ctx: DesignerCtx): TemplateFilter {
  const existing = filters.get(ctx);
  if (existing) return existing;
  const fresh: TemplateFilter = { query: '', category: '', colorway: 'original' };
  filters.set(ctx, fresh);
  return fresh;
}

/** Diacritics and case folded away, so "sretan" finds "Srećan". */
function fold(value: string): string {
  return value.toLocaleLowerCase('sr-RS').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * The shopper's words by template key. Keyed fields carry over as they are;
 * a plain, short text the shopper typed themselves (the quick start's name)
 * stands in for "ime" when nothing is keyed that way.
 */
export function templateFill(answer: DesignAreaAnswer): Record<string, string> {
  const fill: Record<string, string> = {};
  const texts = answer.layers.filter((layer): layer is Extract<DesignLayer, { kind: 'text' }> => layer.kind === 'text' && layer.visible !== false);
  for (const layer of texts) {
    const key = layer.template?.key;
    if (key && /^[a-z0-9-]{1,40}$/.test(key) && layer.text.trim() && !fill[key]) fill[key] = layer.text;
  }
  if (!fill.ime) {
    const plain = texts
      .filter((layer) => !layer.template?.key && layer.text.trim() && !layer.text.includes('\n') && layer.text.length <= 30)
      .sort((a, b) => b.sizeMm - a.sizeMm)[0];
    if (plain) fill.ime = plain.text.trim();
  }
  return fill;
}

export function renderTemplatesPanel(body: HTMLElement, ctx: DesignerCtx): () => void {
  let cancelled = false;
  const filter = filterFor(ctx);
  const answer = ctx.store.value();
  const hasPhoto = answer.layers.some((layer) => layer.kind === 'image' && layer.visible !== false);
  filter.kind ??= hasPhoto && ctx.config.allow.image ? 'photo-text' : 'all';
  // On a dark shirt the dark default layouts would vanish: start in the light colourway.
  if (!filter.colorPicked) {
    const ink = ctx.inkOnProduct?.();
    const rgb = ink && hexToRgb(ink.hex);
    filter.colorway = rgb && luminance(rgb) > 0.5 ? 'light' : 'original';
  }

  renderQuickFields(body, ctx);

  const fontId = ctx.config.text.defaultFontId ?? ctx.fonts.list[0]?.id ?? '';
  // The written name of a monogram layout: the shop's first handwritten face,
  // when it offers one; otherwise the layout keeps a single font.
  const scriptFontId = ctx.fonts.list.find((font) => font.category === 'script' || font.category === 'handwriting')?.id;
  const fonts = { fontId: fontId || 'unused-for-shapes', ...(scriptFontId && scriptFontId !== fontId ? { scriptFontId } : {}) };
  const fill = templateFill(answer);
  const hasFill = Object.keys(fill).length > 0;

  interface Entry {
    id: string;
    name: string;
    description?: string;
    category: string;
    kind: DesignerTemplateKind;
    custom: boolean;
  }
  const entries: Entry[] = [
    ...(ctx.rawConfig.templates ?? []).map((template) => ({ id: template.id, name: template.name, ...(template.description ? { description: template.description } : {}), category: template.category ?? ctx.texts.toolTemplates, kind: designerTemplateKind(template), custom: true })),
    ...DESIGNER_TEMPLATES.map((template) => ({ id: template.id, name: template.name, description: template.description, category: template.category, kind: designerTemplateKind(template), custom: false })),
  ];

  /** The layout as it would look now: their photo (or a soft slot) and their words. */
  function previewOf(entry: Entry, colorway: DesignerColorway): DesignAreaAnswer | undefined {
    const command = { op: 'template' as const, templateId: entry.id, ...fonts, ...(entry.custom ? {} : { colorway }), ...(hasFill ? { fill } : {}) };
    try {
      if (entry.kind === 'text' || hasPhoto) return applyDesignerCommand(ctx.rawConfig, answer, command);
      return designerTemplate(ctx.rawConfig, entry.id, fonts.fontId, colorway, fonts.scriptFontId ?? fonts.fontId, { placeholder: true, ...(hasFill ? { fill } : {}) });
    } catch {
      // Their name may be too long for this layout; the sample still shows it.
      if (!hasFill) return undefined;
      try {
        if (entry.kind === 'text' || hasPhoto) return applyDesignerCommand(ctx.rawConfig, answer, { ...command, fill: undefined });
        return designerTemplate(ctx.rawConfig, entry.id, fonts.fontId, colorway, fonts.scriptFontId ?? fonts.fontId, { placeholder: true });
      } catch { return undefined; }
    }
  }

  // Only what this product can use at all: a card that throws on tap is worse
  // than a card that is not there.
  const usable = entries.filter((entry) => previewOf(entry, 'original') !== undefined);
  const kinds = (['photo-text', 'text', 'photo'] as const).filter((kind) => usable.some((entry) => entry.kind === kind));
  if (filter.kind !== 'all' && !kinds.includes(filter.kind as DesignerTemplateKind)) filter.kind = 'all';

  const kindRow = el('div', 'pzd-template-kinds');
  const colorRow = el('div', 'pzd-template-colors');
  colorRow.setAttribute('role', 'group');
  colorRow.setAttribute('aria-label', ctx.texts.templateColors);
  const chips = el('div');
  const count = el('p', 'pzd-hint');
  const grid = el('div', 'pzd-template-grid');
  const empty = note(ctx.texts.templateNone);
  body.append(kindRow, colorRow, note(ctx.texts.templateNote));
  body.append(searchBox(ctx.texts.templateSearch, filter.query, (query) => {
    filter.query = query;
    paintCards();
  }));
  body.append(chips, count, grid, empty);

  function paintKinds(): void {
    kindRow.textContent = '';
    if (kinds.length < 2) return;
    const label: Record<KindFilter, string> = {
      all: ctx.texts.templateKindAll,
      'photo-text': ctx.texts.templateKindPhotoText,
      text: ctx.texts.templateKindText,
      photo: ctx.texts.templateKindPhoto,
    };
    kindRow.append(tabs([
      ...kinds.map((kind) => ({ id: kind, label: label[kind], on: filter.kind === kind })),
      { id: 'all', label: label.all, on: filter.kind === 'all' },
    ], (id) => {
      filter.kind = id as KindFilter;
      filter.category = '';
      paintKinds();
      paintCards();
    }));
  }

  function paintColors(): void {
    colorRow.textContent = '';
    for (const palette of DESIGNER_COLORWAYS) {
      const button = el('button', 'pzd-colorway');
      button.type = 'button';
      button.title = palette.name;
      button.setAttribute('aria-label', `${ctx.texts.templateColors}: ${palette.name}`);
      button.setAttribute('aria-pressed', String(palette.id === filter.colorway));
      button.style.background = `linear-gradient(135deg, ${palette.ink} 50%, ${palette.accent} 50%)`;
      button.addEventListener('click', () => {
        filter.colorway = palette.id;
        filter.colorPicked = true;
        paintColors();
        paintCards();
      });
      colorRow.append(button);
    }
  }

  let generation = 0;
  const pendingThumbs = new Map<Element, () => void>();
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver((seen) => {
    for (const entry of seen) if (entry.isIntersecting) {
      pendingThumbs.get(entry.target)?.(); pendingThumbs.delete(entry.target); observer?.unobserve(entry.target);
    }
  }, { root: null, rootMargin: '160px' }) : undefined;

  function paintCards(): void {
    const current = ++generation;
    observer?.disconnect();
    pendingThumbs.clear();
    grid.textContent = '';
    const byKind = usable.filter((entry) => filter.kind === 'all' || entry.kind === filter.kind);
    const categories = [...new Set(byKind.map((entry) => entry.category))];
    if (filter.category && !categories.includes(filter.category)) filter.category = '';
    chips.textContent = '';
    if (categories.length > 1) {
      chips.append(tabs([
        { id: '', label: ctx.texts.templateAll, on: !filter.category },
        ...categories.map((category) => ({ id: category, label: category, on: filter.category === category })),
      ], (id) => {
        filter.category = id;
        paintCards();
      }));
    }
    const needle = fold(filter.query.trim());
    const visible = byKind.filter((entry) => (!needle || fold(`${entry.name} ${entry.category} ${entry.description ?? ''}`).includes(needle))
      && (!filter.category || entry.category === filter.category));
    for (const entry of visible) {
      const card = el('button', `pzd-template-card is-${entry.kind}`);
      card.type = 'button';
      card.setAttribute('aria-label', `${ctx.texts.toolTemplates}: ${entry.name}`);
      const thumb = el('canvas');
      thumb.setAttribute('aria-hidden', 'true');
      card.append(thumb, el('strong', undefined, entry.name));
      if (entry.description) card.append(el('small', undefined, entry.description));
      if (entry.kind !== 'text' && !hasPhoto) card.append(el('span', 'pzd-template-badge', ctx.texts.templateNeedsPhoto));
      card.addEventListener('click', () => void apply(entry));
      grid.append(card);
      const render = () => {
        const preview = previewOf(entry, filter.colorway);
        if (preview) void paintThumb(ctx, thumb, preview, () => cancelled || current !== generation);
      };
      if (observer) { pendingThumbs.set(card, render); observer.observe(card); }
      else render();
    }
    empty.hidden = visible.length > 0;
    count.textContent = fillText(ctx.texts.templateCount, { n: visible.length });
  }

  async function apply(entry: Entry): Promise<void> {
    let imageLayerId: string | undefined;
    if (entry.kind !== 'text' && !ctx.store.value().layers.some((layer) => layer.kind === 'image' && layer.visible !== false)) {
      const upload = await pickPhoto(ctx);
      if (!upload) return;
      const layer = freshImageLayer(ctx.rawConfig, upload, ctx.addCount());
      ctx.dispatch({ type: 'add', layer, select: false });
      imageLayerId = layer.id;
    }
    const words = templateFill(ctx.store.value());
    const command = {
      op: 'template' as const,
      templateId: entry.id,
      ...fonts,
      ...(entry.custom ? {} : { colorway: filter.colorway }),
      ...(imageLayerId ? { imageLayerId } : {}),
    };
    // Their words when they fit; the layout's own sample words when they do not.
    const withWords = Object.keys(words).length && run(ctx, { ...command, fill: words }, undefined, { quiet: true });
    if (withWords || run(ctx, command)) {
      ctx.dispatch({ type: 'select', id: null });
      ctx.toast(ctx.texts.templateApplied, 'info');
    }
  }

  paintKinds();
  paintColors();
  paintCards();

  return () => {
    cancelled = true;
    observer?.disconnect();
    pendingThumbs.clear();
  };
}

/**
 * The template objects of the CURRENT design, as editable fields.
 *
 * Text layers get an input with the authored label above it; image and artwork
 * layers get a button into the picker. A `mustReplace` field that still holds
 * the authored content wears a red "Obavezno" pill, which is the same state
 * the checkout refuses on.
 */
function renderQuickFields(body: HTMLElement, ctx: DesignerCtx): void {
  const layers = ctx.store.value().layers.filter((layer) => layer.template || (layer.kind === 'text' && !layer.locked));
  if (!layers.length) return;
  const box = field(ctx.texts.templateFields);
  for (const layer of layers) {
    const row = el('div', 'pzd-quick-field');
    const head = el('div', 'pzd-quick-head');
    head.append(el('span', 'pzd-lbl', layerLabel(layer) ?? shortText(layer)));
    if (templateUnreplaced(layer)) {
      const pill = el('span', 'pzd-pill is-must', ctx.texts.mustReplaceBadge);
      head.append(pill);
    }
    row.append(head);
    if (layer.kind === 'text') {
      const input = el('textarea', 'pzd-textarea is-short');
      input.value = layer.text;
      input.rows = 2;
      input.maxLength = ctx.config.text.maxChars;
      input.setAttribute('aria-label', layerLabel(layer) ?? ctx.texts.addText);
      input.disabled = Boolean(layer.locked || layer.interactive === false);
      input.addEventListener('input', () => run(ctx, {
        op: 'text',
        ids: [layer.id],
        patch: { text: input.value },
      }, `text:${layer.id}`));
      row.append(input);
    } else {
      const button = el('button', 'pzd-command');
      button.type = 'button';
      button.textContent = ctx.texts.changeImage;
      button.addEventListener('click', () => {
        ctx.dispatch({ type: 'select', id: layer.id });
        if (layer.kind === 'image') startReplace(ctx, layer.id);
        ctx.openTool(layer.kind === 'image' ? 'image' : 'icons');
      });
      row.append(button);
    }
    box.wrap.append(row);
  }
  body.append(box.wrap);
}

function shortText(layer: DesignLayer): string {
  if (layer.kind !== 'text') return layer.kind;
  return layer.text.split('\n')[0]?.slice(0, 28) || layer.kind;
}
